/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

"use client";

import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { Sparkles } from "lucide-react";

interface TeamMember {
	name: string;
	image: string;
	role: string;
	bio: string;
	accent: string;
}

interface Props {
	member: TeamMember;
}

const TeamCard: React.FC<Props> = ({ member }) => {
	const [isHovered, setIsHovered] = useState(false);
	const cardRef = useRef<HTMLDivElement>(null);
	const glowRef = useRef<HTMLDivElement>(null);

	// GSAP hover lift on card
	useEffect(() => {
		if (!cardRef.current) return;

		gsap.to(cardRef.current, {
			y: isHovered ? -8 : 0,
			scale: isHovered ? 1.02 : 1,
			duration: 0.45,
			ease: "power3.out",
		});
	}, [isHovered]);

	// GSAP ambient pulse on the glow
	useEffect(() => {
		if (!glowRef.current) return;

		const tween = gsap.to(glowRef.current, {
			opacity: 0.85,
			scale: 1.08,
			repeat: -1,
			yoyo: true,
			duration: 2.4,
			ease: "sine.inOut",
		});

		return () => {
			tween.kill();
		};
	}, []);

	return (
		<div
			className="relative w-[620px] max-w-full"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}>
	

			{/* Hover boost glow */}
			<motion.div
				aria-hidden
				className="pointer-events-none absolute -inset-2 rounded-3xl blur-3xl"
				animate={{ opacity: isHovered ? 0.7 : 0 }}
				transition={{ duration: 0.4 }}
				style={{
					background:
						"radial-gradient(60% 60% at 30% 50%, rgba(240,154,56,0.55), transparent 70%), radial-gradient(60% 60% at 80% 60%, rgba(124,58,237,0.55), transparent 70%)",
				}}
			/>

			{/* Below `sm` the 50/50 split squeezed a portrait photo into a ~150px
			    column and the bio into another, which is what made both look
			    stretched. Stacking the card and giving the photo a portrait 4:5
			    frame keeps the aspect ratio honest and the text readable. From `sm`
			    up the original side-by-side 500px card is reproduced exactly. */}
			<div
				ref={cardRef}
				className="relative flex flex-col overflow-hidden rounded-3xl border border-white/60 bg-white shadow-[0_25px_60px_-20px_rgba(59,0,146,0.35)] sm:h-[500px] sm:flex-row">
				{/* LEFT — image (50% width, full height from `sm` up).
				    The source photos are mixed orientation: ve/sk/dipak are 3:4–4:5
				    portraits while Mukun/Vina are 3:2–4:3 landscapes. A landscape
				    frame therefore cut ~44% off the height of the portraits, which
				    is what clipped people's faces. A 4:5 frame sits within a few
				    percent of the portraits' native ratio and crops the landscapes
				    horizontally instead, where they have room to spare. Every face
				    in the set falls between roughly 7% and 62% of image height, so
				    biasing the crop point to 25% clears all of them at both edges. */}
				<div className="relative aspect-4/5 w-full shrink-0 overflow-hidden sm:aspect-auto sm:h-full sm:w-1/2">
					<motion.img
						src={member.image}
						alt={member.name}
						loading="lazy"
						decoding="async"
						className="h-full w-full object-cover object-[center_25%] sm:object-center"
						animate={{ scale: isHovered ? 1.08 : 1 }}
						transition={{ duration: 0.6, ease: "easeOut" }}
					/>

					{/* Soft accent ring */}
					<motion.div
						aria-hidden
						className="pointer-events-none absolute inset-3 rounded-2xl border"
						animate={{
							borderColor:
								isHovered ?
									"rgba(240,154,56,0.7)"
								:	"rgba(255,255,255,0.45)",
						}}
						transition={{ duration: 0.35 }}
					/>
				</div>

				{/* RIGHT — content (50% width from `sm` up, scrollable when bio overflows) */}
				<div className="relative flex w-full min-w-0 flex-col p-5 sm:h-full sm:w-1/2">
					<motion.h3
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.05 }}
						className="text-xl font-bold leading-tight text-[#1a1033] sm:text-2xl">
						{member.name}
					</motion.h3>
					<motion.p
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.1 }}
						className="mt-1 text-sm font-semibold text-[#7c3aed]">
						{member.role}
					</motion.p>

					{/* On phones the bio flows naturally (no inner scroll area, which is
					    awkward to reach on touch); from `sm` it scrolls inside the fixed
					    card height exactly as before. */}
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.15 }}
						className="team-card-scroll mt-3 min-h-0 text-[13.5px] leading-relaxed text-gray-600 sm:mt-4 sm:flex-1 sm:overflow-y-auto sm:pr-2">
						<p>{member.bio}</p>
					</motion.div>

					{/* Animated accent line */}
					<motion.div
						aria-hidden
						className="mt-4 h-[2px] w-12 origin-left shrink-0 rounded-full bg-linear-to-r from-[#f09a38] via-[#f7c977] to-transparent"
						animate={{ scaleX: isHovered ? 2.4 : 1 }}
						transition={{ duration: 0.4, ease: "easeOut" }}
					/>
				</div>
			</div>

			<style>{`
				.team-card-scroll::-webkit-scrollbar {
					width: 5px;
				}
				.team-card-scroll::-webkit-scrollbar-track {
					background: transparent;
				}
				.team-card-scroll::-webkit-scrollbar-thumb {
					background: linear-gradient(180deg, #f09a38, #7c3aed);
					border-radius: 9999px;
				}
				.team-card-scroll {
					scrollbar-width: thin;
					scrollbar-color: #f09a38 transparent;
				}
			`}</style>
		</div>
	);
};

export default TeamCard;
